/**
 * This module contains a set of domain-agnostic card components that are currently exclusively
 * used for prompt suggestions. If there ends up being more use cases, this module can be relocated
 * to a shared directory.
 */
import {Color} from '@benchling/nucleus-ui';
import {Box, Group, Paper, Space, Stack, Text, Title} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import {ChipSize} from 'ui-library/chips/Chip';
import LoadingChip from 'ui-library/chips/LoadingChip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

/**
 * Render a collection of {@link Card}s.
 */
export function CardGallery({
  title,
  children,
}: {
  title: React.ReactNode;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <Stack p="md" spacing="sm">
      <Title order={5} fw={600}>
        {title}
      </Title>
      {children}
    </Stack>
  );
}

/**
 * Render a single card.
 *
 * `Card` should be rendered within a {@link CardGallery}.
 */
export function Card(props: {
  children: React.ReactNode;
  icon: React.ReactNode | null;
  onClick?: () => void;
  submitting?: boolean;
  tabIndex?: number;
}): JSX.Element {
  const {icon, children, onClick, submitting, tabIndex} = props;

  return (
    <Paper
      component="button"
      className={css(
        styles.card,
        onClick && !submitting && styles.cardClickable,
        submitting && styles.cardSubmitting
      )}
      withBorder={true}
      radius="md"
      shadow="xs"
      onClick={onClick}
      p="md"
      disabled={submitting || !onClick}
      tabIndex={tabIndex}
    >
      <Group align="baseline">
        {icon && <Box style={{flex: 'none', color: Color.GRAY_500}}>{icon}</Box>}
        <Box style={{flex: 1, color: Color.GRAY_900, minWidth: 0 /* prevent flex item from overflowing */}}>
          {children}
        </Box>
        {onClick && (
          <Box style={{flex: 'none', color: Color.GRAY_700}}>
            <BenchlingIcon icon="arrow-up-right" />
          </Box>
        )}
      </Group>
    </Paper>
  );
}

export function LoadingCard(): JSX.Element {
  return (
    <Card icon={null}>
      <LLMLoadingMessage text="Loading..." />
      <Space h="xs" />
      <Text
        style={
          // We want to render content similar to a the non-loading card to avoid layout shifts.
          // It is insufficient to render just a `nbsp because Chips are slightly taller than Text line-height.
          // We render a LoadingChip, which is semantically correct, but we'll hide it since we already have
          // LLMLoadingMessage.
          {visibility: 'hidden'}
        }
      >
        <LoadingChip size={ChipSize.TABLE_CELL} />
      </Text>
    </Card>
  );
}

const cardSubmitting = {
  borderColor: Color.PURPLE_600,
  backgroundColor: Color.PURPLE_100,
};

const cardHover = {
  boxShadow: '0px 2px 6px 0px #00000040',
  // Transform makes the card "lift", revealing the shadow. While this is more visually pleasing by
  // embracing material design, it does not match our design system.
  // transform: 'translateY(-1px)',
};

const styles = StyleSheet.create({
  card: {
    textAlign: 'left',
    userSelect: 'none',
    cursor: 'wait',
    transition: 'box-shadow 0.1s ease-in-out, transform 0.1s ease-in-out',
    ':hover': {
      ...cardHover,
    },
    ':focus': {
      ...cardHover,
    },
    ':active': {
      ...cardSubmitting,
    },
  },
  cardSubmitting,
  cardClickable: {
    cursor: 'pointer',
  },
});
